import { useState, useEffect } from 'react';
import { useTheme } from './Theme';
import Login from './pages/Login';
import Account from './pages/Account';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import Permissions from './pages/Permissions';
import HRDocuments from './pages/HRDocuments';
import ProductionPhotos from './pages/ProductionPhotos';
import DistroTaproomOrders from './pages/DistroTaproomOrders';
import SOPsChecklists from './pages/SOPsChecklists';
import Checklists from './pages/Checklists';
import LabelInventory from './pages/LabelInventory';
import TaproomInventory from './pages/TaproomInventory';
import TaproomInspections from './pages/TaproomInspections';
import Recipes from './pages/Recipes';
import CocktailKeeper from './pages/CocktailKeeper';
import SalesCRM from './pages/SalesCRM';
import ProductionSchedule from './pages/ProductionSchedule';
import EightySixedCustomers from './pages/EightySixedCustomers';
import PackagingLog from './pages/PackagingLog';
import CoffeeKeeper from './pages/CoffeeKeeper';
import ProductionWeekly from './pages/ProductionWeekly';
import EquipmentManuals from './pages/EquipmentManuals';
import TankMaintenance from './pages/TankMaintenance';
import CoffeeSiteManager from './pages/CoffeeSiteManager';
import SpiritsOrdering from './pages/SpiritsOrdering';
import SpiritsInventory from './pages/SpiritsInventory';
import CoffeeInventory from './pages/CoffeeInventory';
import IncidentReports from './pages/IncidentReports';
import Onboarding from './pages/Onboarding';
import EventPlanner from './pages/EventPlanner';
import Notifications from './pages/Notifications';
import { syncBadge, clearBadge } from './badge';
import { checkVersion } from './version';
import { startActivityTracking } from './activity';
import { parseAppPath, buildPath } from './navPath';

const API = process.env.REACT_APP_API_URL || '';

function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState('dashboard');
  const [pageProps, setPageProps] = useState({});
  const [tools, setTools] = useState([]); // my-tools; used to resolve canUpload on prop-less navigations
  const [loading, setLoading] = useState(true);
  const { setTheme } = useTheme();

  useEffect(() => {
    // Parse the path AND query string — the query carries a deep-link target
    // (e.g. `/incident-reports?id=45`) on a cold load, which is the path a push
    // notification click takes (the service worker navigates to the full url).
    const { pageName: initialPage, focus } = parseAppPath(window.location.pathname + window.location.search);
    fetch(`${API}/api/me`, { credentials: 'include' })
      .then((res) => res.ok ? res.json() : null)
      .then(async (data) => {
        if (data) {
          setUser(data);
          if (data.theme_preference) setTheme(data.theme_preference);
          syncBadge();
          startActivityTracking();
          // Fetch tools so we can restore canUpload on direct tool URLs and resolve
          // it for prop-less navigations (e.g. clicking a notification).
          let toolList = [];
          try {
            const r = await fetch(`${API}/api/my-tools`, { credentials: 'include' });
            if (r.ok) toolList = await r.json();
          } catch {}
          setTools(toolList);
          const toolEntry = toolList.find(t => t.slug === initialPage);
          const props = {
            ...(toolEntry ? { canUpload: toolEntry.has_upload_permission } : {}),
            ...(focus ? { focus } : {}),
          };
          setPage(initialPage);
          setPageProps(props);
          // Keep the full URL (incl. query) so a refresh re-deep-links.
          window.history.replaceState({ pageName: initialPage, props }, '', buildPath(initialPage, focus));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await fetch(`${API}/api/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
    setPage('dashboard');
    setPageProps({});
    clearBadge();
    window.history.replaceState({ pageName: 'dashboard', props: {} }, '', '/');
  };

  const handleNavigate = (pageName, props = {}) => {
    // Auto-resolve canUpload for tool pages when the caller didn't pass it (e.g.
    // notification clicks, the Account "My Access" chips) so manage-only tabs and
    // buttons aren't hidden. An explicit canUpload (from the Dashboard) still wins.
    let resolved = props;
    if (props.canUpload === undefined) {
      const t = tools.find((x) => x.slug === pageName);
      if (t) resolved = { ...props, canUpload: t.has_upload_permission };
    }
    window.history.pushState({ pageName, props: resolved }, '', buildPath(pageName, resolved.focus));
    setPage(pageName);
    setPageProps(resolved);
  };

  useEffect(() => {
    const onPop = (e) => {
      try {
        const state = e.state;
        if (state && state.pageName) {
          setPage(state.pageName);
          setPageProps(state.props || {});
        } else {
          setPage('dashboard');
          setPageProps({});
        }
      } catch (err) {
        setPage('dashboard');
        setPageProps({});
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // On launch and whenever the app regains focus: re-sync the badge, and check
  // whether a newer build has been deployed (hard-reload if so).
  useEffect(() => {
    checkVersion();
    const onVis = () => { if (document.visibilityState === 'visible') { syncBadge(); checkVersion(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const handleLogin = async (userData) => {
    setUser(userData);
    startActivityTracking();
    // Hydrate the full profile (pronouns/photo/theme) and apply the saved theme.
    try {
      const meRes = await fetch(`${API}/api/me`, { credentials: 'include' });
      if (meRes.ok) {
        const full = await meRes.json();
        setUser(full);
        if (full.theme_preference) setTheme(full.theme_preference);
      }
    } catch {}
    const { pageName: currentPage, focus } = parseAppPath(window.location.pathname + window.location.search);
    try {
      const r = await fetch(`${API}/api/my-tools`, { credentials: 'include' });
      if (r.ok) {
        const toolList = await r.json();
        setTools(toolList);
        if (currentPage !== 'dashboard') {
          const toolEntry = toolList.find(t => t.slug === currentPage);
          setPage(currentPage);
          setPageProps({
            ...(toolEntry ? { canUpload: toolEntry.has_upload_permission } : {}),
            ...(focus ? { focus } : {}),
          });
        }
      }
    } catch {}
  };

  if (loading) return null;
  if (!user) return <Login onLogin={handleLogin} />;
  if (page === 'account') return <Account user={user} onBack={() => handleNavigate('dashboard')} onUserUpdate={setUser} onNavigate={handleNavigate} />;
  if (page === 'notifications') return <Notifications user={user} onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} />;
  if (page === 'usermanagement') return <UserManagement user={user} onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} />;
  if (page === 'permissions') return <Permissions onBack={() => handleNavigate('usermanagement')} onHome={() => handleNavigate('dashboard')} />;
  if (page === 'hr-documents') return <HRDocuments user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'production-photos') return <ProductionPhotos user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'distro-taproom-orders') return <DistroTaproomOrders user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'sops') return <SOPsChecklists user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'coffee-checklist')  return <Checklists key="coffee"  department="coffee"  user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'bar-checklist')     return <Checklists key="bar"     department="bar"     user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'foodlab-checklist') return <Checklists key="foodlab" department="foodlab" user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'label-inventory') return <LabelInventory user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'taproom-inventory') return <TaproomInventory user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'taproom-inspections') return <TaproomInspections user={user} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'recipes') return <Recipes user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'cocktail-keeper') return <CocktailKeeper user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'sales-crm') return <SalesCRM user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'production-schedule') return <ProductionSchedule user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === '86ed-customers') return <EightySixedCustomers user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'packaging-log') return <PackagingLog user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'coffee-keeper') return <CoffeeKeeper user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'production-weekly') return <ProductionWeekly user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'equipment-manuals') return <EquipmentManuals user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'tank-maintenance') return <TankMaintenance user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'coffee-site') return <CoffeeSiteManager user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'spirits-ordering') return <SpiritsOrdering user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'spirits-inventory') return <SpiritsInventory user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'coffee-inventory') return <CoffeeInventory user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'incident-reports') return <IncidentReports user={user} canUpload={pageProps.canUpload} focus={pageProps.focus} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'onboarding') return <Onboarding user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;
  if (page === 'event-planner') return <EventPlanner user={user} canUpload={pageProps.canUpload} onBack={() => handleNavigate('dashboard')} />;

  return <Dashboard user={user} onLogout={handleLogout} onNavigate={handleNavigate} />;
}

export default App;